// utils/api.js
import axios from "axios";
import { URLS, BASE_URL } from "./urls";

let accessToken = localStorage.getItem("token");

// Functions to manage in-memory access token
export const setAccessToken = (token) => {
  accessToken = token;
  localStorage.setItem("token", token);
};

export const getAccessToken = () => accessToken;

// Create Axios instance
const api = axios.create({
  baseURL: BASE_URL, 
  withCredentials: true, // send HttpOnly cookies
  headers: { "Content-Type": "application/json" },
});

// Request interceptor: attach access token
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      console.log("⚠️ No access token set for request");
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: refresh token automatically on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If it's a 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Call refresh endpoint from URLS
        const res = await axios.post(URLS.REFRESH, {}, { withCredentials: true });

        const newAccessToken = res.data.accessToken;
        setAccessToken(newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed, logging out:", refreshError);
        
        // Clear tokens and redirect to login
        localStorage.clear();
        accessToken = null;
        window.location.href = "/signin";
        
        return Promise.reject(refreshError);
      }
    }

    // If it's a 401 and we already retried (or it's not a retryable request)
    if (error.response?.status === 401) {
      console.warn("Unauthorized, logging out...");
      localStorage.clear();
      accessToken = null;
      
      // Only redirect if not already on the signin page
      if (window.location.pathname !== "/signin") {
        window.location.href = "/signin";
      }
    }

    return Promise.reject(error);
  }
);

export default api;
